import React, { memo, useEffect, useMemo, useState } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import useLocalizedTime from '../../hooks/useLocalizedTime';
import {
    ORDER_STATUS,
    TICKET_TYPE,
    USAGE_TICKET_STATUS,
    TICKET_STATUS,
    SECTION_TYPE,
    DISTRIBUTION_TYPE,
} from '../../utils/Constants';
import {
    getValueByLanguage,
    shouldDisplayActivateDeadlineAt,
    shouldDisplayActivatedAt,
    isUsableTicket,
    isReserveTicket,
    isDistributed,
    isReservationCanceled,
    isRefunded,
    isAfterActivateDeadline,
} from '../../utils';

import ComboTicketList from '../BoughtTicketList/ComboTicketList';
import UseTicketButton from '../UseTicketButton/UseTicketButton';
import TextBreaks from '../TextBreaks/TextBreaks';
import IcNext from '../../assets/imgs/ic_top.svg?react';
import usePurchaseTicket from '../../hooks/usePurchaseTicket';
import CenterLoading from '../CenterLoading/CenterLoading';

import styles from './style.module.css';
import { OrderTicket, OrderTicketList } from 'types';
import { OrderUsageTicket } from '@shared/orders/get/types';

/**
 * 購入済みチケットの１つ
 */
type PurchasedTicketItemProps = {
    ticket: OrderTicketList;
    to: string;
};

const PurchasedTicketItem = memo(({ ticket, to }: PurchasedTicketItemProps) => {
    const [orderTicket, setOrderTicket] = useState<OrderTicketList>();
    const { t } = useTranslation();
    const { formatTime } = useLocalizedTime();
    const navigate = useNavigate();
    const { distributionsOrderTicket, isLoading } = usePurchaseTicket();

    const isSingleTicket = !ticket.isCombination;

    useEffect(() => {
        if (isSingleTicket) {
            setOrderTicket({ ...ticket, ...ticket.comboItems[0] });
        } else {
            setOrderTicket({ ...ticket });
        }
    }, [ticket, isSingleTicket]);

    const { reserveOn, originStopName, destStopName } = useMemo(() => {
        const reservationTicket = orderTicket?.comboItems.find((item) => item.isReserve);
        const reserveOn = reservationTicket?.orderUsageTickets?.[0]?.reserveOn;
        const originStopName = reservationTicket?.orderUsageTickets?.[0]?.originStopName;
        const destStopName = reservationTicket?.orderUsageTickets?.[0]?.destStopName;
        return {
            reserveOn,
            originStopName,
            destStopName,
        };
    }, [orderTicket]);

    // クリックして使用前詳細画面へ遷移
    const handleTicketClick = () => {
        const orderId = ticket.orderId;
        const orderTicketId = isSingleTicket ? orderTicket?.orderTicketId : null;
        navigate(to, { state: { orderId: orderId, orderTicketId: orderTicketId } });
    };

    const couponUseText = (couponTickets: OrderUsageTicket[]) => {
        if (couponTickets?.length > 0) {
            const beforeUseTotal = couponTickets.filter(
                (coupon) => coupon.status === USAGE_TICKET_STATUS.BOUGHT,
            ).length;
            const usingTotal = couponTickets.filter((coupon) => coupon.status === USAGE_TICKET_STATUS.ACTIVATED).length;
            const usedTotal = couponTickets.filter((coupon) => coupon.status === USAGE_TICKET_STATUS.USED).length;
            return (
                <>
                    <p>
                        {beforeUseTotal > 0
                            ? `${t('ticket-detail.before-use')}: ${t('ticket-detail.coupon-count', {
                                  count: beforeUseTotal,
                              })}(${t('ticket-detail.coupon-amount')}${t('ticket-detail.coupon-count', {
                                  count: couponTickets.length,
                              })})`
                            : `${t('ticket-detail.used')}: ${t('ticket-detail.coupon-count', { count: usedTotal })}`}
                    </p>
                    <p>
                        {usingTotal > 0
                            ? `${t('ticket-detail.in-use')}: ${t('ticket-detail.coupon-count', { count: usingTotal })}`
                            : ''}
                    </p>
                </>
            );
        } else {
            return '';
        }
    };

    const DistributionsBtn = ({ orderTicket }: { orderTicket: OrderTicketList }) => {
        const text =
            orderTicket?.distributionType === DISTRIBUTION_TYPE.REQUIRED && !isDistributed(orderTicket)
                ? t('button.distributions')
                : t('button.distributions-info-confirm');
        const handleBtnClick = async () => {
            if (isDistributed(orderTicket)) {
                navigate('/bought/distributions/info', { state: { orderTicketId: orderTicket?.orderTicketId } });
            } else {
                const {
                    remainingNumberAdults,
                    remainingNumberCustom1,
                    remainingNumberCustom2,
                    remainingNumberCustom3,
                } = orderTicket?.remainingOrderDistribution ?? {};
                const orderTicketId = await distributionsOrderTicket({
                    orderTicketId: orderTicket?.orderTicketId,
                    distributeCount: {
                        numberAdults: remainingNumberAdults ?? 0,
                        numberCustom1: remainingNumberCustom1 ?? 0,
                        numberCustom2: remainingNumberCustom2 ?? 0,
                        numberCustom3: remainingNumberCustom3 ?? 0,
                    },
                });
                if (orderTicketId) {
                    navigate('/bought/distributions/info', { state: { orderTicketId: orderTicket?.orderTicketId } });
                }
            }
        };
        return (
            <div className={styles.distributionsBtn} onClick={() => void handleBtnClick()}>
                <p className={styles.distributionsBtnText}>{text}</p>
                <IcNext className={styles.distributionsBtnIcon} />
            </div>
        );
    };

    const isDisplayDistributionBtn = (orderTicket?: OrderTicketList) => {
        if (!orderTicket) {
            return false;
        }
        //予約キャンセルされたこと
        if (isReservationCanceled(orderTicket as unknown as OrderTicket)) return false;
        const { orderStatus, activateDeadlineAt, status } = orderTicket;
        //チケットの購入状態が「購入済み」であること
        if (orderStatus !== ORDER_STATUS.BOUGHT) {
            return false;
        }
        //使用期限切れチェック orderStatus -> orders.status, status -> orderTickets.status
        if (isAfterActivateDeadline(activateDeadlineAt) || status === TICKET_STATUS.ACTIVATE_DEADLINE_PASSED) {
            return false;
        }
        return (
            (orderTicket?.distributionType === DISTRIBUTION_TYPE.ENABLE && isDistributed(orderTicket)) ||
            orderTicket?.distributionType === DISTRIBUTION_TYPE.REQUIRED
        );
    };

    return (
        <>
            {isLoading && <CenterLoading />}
            {orderTicket && (
                <div className={styles.contentWrapper}>
                    <div className={styles.ticketBorder}>
                        <div className={isUsableTicket(ticket) ? styles.content : styles.used}>
                            <div className={styles.info} onClick={handleTicketClick}>
                                {/* 単券の場合に販売権種名を表示する */}
                                {orderTicket?.comboItems?.length === 1 && orderTicket?.saleTicketName && (
                                    <p className={styles.saleTicketName}>
                                        <TextBreaks
                                            text={getValueByLanguage(orderTicket.saleTicketName, i18n.language)}
                                        />
                                    </p>
                                )}
                                <p className={styles.title}>
                                    <TextBreaks text={getValueByLanguage(orderTicket.ticketName, i18n.language)} />
                                </p>
                                {isSingleTicket &&
                                    isReserveTicket(orderTicket) &&
                                    orderTicket.sectionType === SECTION_TYPE.MULTI &&
                                    originStopName &&
                                    destStopName && (
                                        <p className={styles.saleTicketName}>
                                            <TextBreaks text={getValueByLanguage(originStopName, i18n.language)} />
                                            <span style={{ margin: '0 5px' }}>→</span>
                                            <TextBreaks text={getValueByLanguage(destStopName, i18n.language)} />
                                        </p>
                                    )}
                                <div className={styles.textContent}>
                                    {isSingleTicket && (
                                        <>
                                            {/*使用期限*/}
                                            {isReserveTicket(orderTicket)
                                                ? reserveOn && (
                                                      <p className={styles.text}>
                                                          {t('reservation.usage-time') +
                                                              ': ' +
                                                              t('daytime.tilde', {
                                                                  time: formatTime(reserveOn, 'lll'),
                                                              })}
                                                      </p>
                                                  )
                                                : orderTicket.activateDeadlineAt &&
                                                  shouldDisplayActivateDeadlineAt(
                                                      orderTicket as unknown as OrderTicket,
                                                  ) && (
                                                      <p className={styles.text}>
                                                          {t('ticket-detail.activate-deadline') +
                                                              ': ' +
                                                              t('daytime.until', {
                                                                  time: formatTime(
                                                                      orderTicket.activateDeadlineAt,
                                                                      'lll',
                                                                  ),
                                                              })}
                                                      </p>
                                                  )}
                                            {/*使用期限切れ*/}
                                            {orderTicket.status === TICKET_STATUS.ACTIVATE_DEADLINE_PASSED && (
                                                <p className={styles.text}>{t('ticket-detail.expired')}</p>
                                            )}

                                            {/*使用日時*/}
                                            {shouldDisplayActivatedAt(orderTicket as unknown as OrderTicket) &&
                                                orderTicket.orderUsageTickets?.[0]?.activatedAt &&
                                                !isReserveTicket(orderTicket) && (
                                                    <p className={styles.text}>
                                                        {t('ticket-detail.activated-date') +
                                                            ': ' +
                                                            formatTime(
                                                                orderTicket.orderUsageTickets[0]?.activatedAt,
                                                                'lll',
                                                            )}
                                                    </p>
                                                )}
                                            {/*有効期限*/}
                                            {shouldDisplayActivatedAt(orderTicket as unknown as OrderTicket) &&
                                                orderTicket.orderUsageTickets?.[0]?.expiredAt && (
                                                    <p className={styles.text}>
                                                        {t('ticket-detail.expired-at') +
                                                            ': ' +
                                                            t('daytime.until', {
                                                                time: formatTime(
                                                                    orderTicket.orderUsageTickets[0]?.expiredAt,
                                                                    'lll',
                                                                ),
                                                            })}
                                                    </p>
                                                )}
                                            {/*使用終了している場合は*/}
                                            {orderTicket.orderUsageTickets?.[0]?.terminatedAt && (
                                                <p className={styles.text}>{t('ticket-detail.used')}</p>
                                            )}
                                        </>
                                    )}
                                    {/*購入日時*/}
                                    {orderTicket.purchasedAt && (
                                        <p className={styles.text}>
                                            {t('ticket-detail.purchased-date') +
                                                ': ' +
                                                formatTime(orderTicket.purchasedAt, 'lll')}
                                        </p>
                                    )}
                                    {/*払い戻し日時*/}
                                    {orderTicket.refundedAt && isRefunded(orderTicket) && (
                                        <p className={styles.text}>
                                            {t('ticket-detail.refunded-date') +
                                                ': ' +
                                                formatTime(orderTicket.refundedAt, 'lll')}
                                        </p>
                                    )}
                                    {isSingleTicket &&
                                        orderTicket.ticketType === TICKET_TYPE.COUPON &&
                                        orderTicket.orderUsageTickets && (
                                            <div className={styles.text}>
                                                {couponUseText(orderTicket.orderUsageTickets)}
                                            </div>
                                        )}
                                </div>
                                <div className={styles.toDetailBtn}>
                                    <p className={styles.distributionsBtnText}>{t('ticket-detail.detail')}</p>
                                    <IcNext className={styles.distributionsBtnIcon} />
                                </div>
                            </div>
                            {isSingleTicket && (
                                <>
                                    <div className={styles.divider} />
                                    {isDisplayDistributionBtn(orderTicket) ? (
                                        <DistributionsBtn orderTicket={orderTicket} />
                                    ) : (
                                        <UseTicketButton ticket={orderTicket} />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    {!isSingleTicket && (
                        <div className={styles.subSetTicketLayout}>
                            <div className={styles.subSetTicketLayoutHeaderLine}></div>
                            <div className={styles.subSetTicketLayoutContent}>
                                <ComboTicketList orderTicket={orderTicket} />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
});

export default PurchasedTicketItem;
